import { Placeholder } from "@/components/Placeholder";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/assets`;

/**
 * Products / "Built in Oman. Running right now."
 *
 * Items with `image` render a real product screenshot; items without one
 * fall back to a labelled grey placeholder box. Placeholder ids are
 * named after the product so exports match the filename.
 *
 * Copy notes:
 * - fleet-dispatch-platform status is "Ready for launch", not "Live" —
 *   Twasal Awasal is pre-launch everywhere else on the site. Do NOT
 *   reinstate the "700+ deliveries per day" claim without owner sign-off.
 * - meyar-grc status is "In build", not "pilot deployment" — no pilot
 *   is confirmed.
 */
const ITEMS = [
  {
    id: "fleet-dispatch-platform",
    image: {
      base: "fleet-dispatch-platform",
      alt: "A Twasal Awasal delivery driver handing a parcel to a customer in traditional Omani dress, with his young son beside him and a branded red delivery van behind, outside a traditional Omani building.",
    },
    title: "Fleet dispatch platform",
    a: "Driver app in English & Arabic.",
    b: "Customer app, driver app and a shipper portal.",
    status: "Logistics · Oman · Ready for launch",
  },
  {
    id: "showroom-system",
    image: {
      base: "showroom-system",
      alt: "A multi-brand vehicle showroom with rows of SUVs and sedans on display, sales operators at a desk with dual monitors, and customers in traditional Omani dress being helped by a sales agent.",
    },
    title: "Multi-brand showroom system",
    a: "Sales, service and parts in one record.",
    b: "Custom finance journey per brand.",
    status: "Automotive · Oman · Live",
  },
  {
    id: "warehouse-inventory",
    image: {
      base: "warehouse-inventory",
      alt: "An aerial view down a large distribution warehouse with tall pallet racking labelled by aisle, workers in high-visibility vests using tablets, forklifts moving pallets, WMS dashboards on wall screens and a Global Logistics box truck at the loading dock.",
    },
    title: "Warehouse & inventory",
    a: "Two sites, mobile picking, cycle counts.",
    b: "Nightly reconciliation to finance.",
    status: "Distribution · Asia & USA · Live",
  },
  {
    id: "meyar-grc",
    image: {
      base: "meyar-grc",
      alt: "Omani colleagues in a boardroom reviewing the Meyar risk and compliance platform on a wall display in Arabic, showing an 85% risk-reduction metric, a heat-map of risks and an audit status marked complete and compliant.",
    },
    title: "Meyar GRC",
    a: "ISO 27001 controls with evidence attached.",
    b: "Board-ready risk register.",
    status: "GRC · In build",
  },
  {
    id: "marketplace-commerce",
    image: {
      base: "marketplace-commerce",
      alt: "A woman in a hijab at a home desk browsing an online marketplace on a laptop, with product listings and a shop-by-category sidebar visible on screen.",
    },
    title: "Marketplace commerce",
    a: "Multi-vendor, multi-currency.",
    b: "Payouts calculated per contract.",
    status: "E-commerce · In build",
  },
  {
    id: "field-service-scheduling",
    image: {
      base: "field-service-scheduling",
      alt: "A logistics dispatch control room where operators monitor a wall of screens showing a live operations dashboard, route maps and CCTV of a busy yard of parked trucks and shipping containers visible through the window behind them.",
    },
    title: "Field service scheduling",
    a: "Skills matching and route packing.",
    b: "Customer-visible ETAs.",
    status: "Facilities · USA · Live",
  },
];

export function Proof() {
  return (
    <section data-testid="products-section" className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-20 lg:px-6 lg:py-28">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Products</div>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Built in Oman. Running right now.
          </h2>
          <p className="mt-4 text-base text-wafa-ink/70 lg:text-lg">Platforms we designed, built and still operate — with more on the bench.</p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ITEMS.map((it) => (
            <article
              key={it.id}
              data-testid={`product-card-${it.id}`}
              className="group flex flex-col rounded-lg border border-wafa-deep/10 bg-white p-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(14,79,58,0.2)]"
            >
              {it.image ? (
                <picture>
                  {/* Single candidate WebP + JPG fallback. Do NOT add a
                      second -1280w arm without a real source at that width —
                      the SPA catch-all returns index.html at 200 for missing
                      /assets URLs and browsers render that as a broken image. */}
                  <source
                    type="image/webp"
                    srcSet={`${ASSET_BASE}/${it.image.base}-640.webp`}
                  />
                  <img
                    src={`${ASSET_BASE}/${it.image.base}.jpg`}
                    alt={it.image.alt}
                    width="640"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    data-testid={`product-card-image-${it.id}`}
                    className="block aspect-[8/5] w-full rounded-md object-cover"
                  />
                </picture>
              ) : (
                <Placeholder id={it.id} width={640} height={400} />
              )}
              <div className="mt-4">
                <div className="font-heading text-lg font-semibold text-wafa-deep">{it.title}</div>
                <ul className="mt-2 space-y-1 text-sm text-wafa-ink/75">
                  <li>· {it.a}</li>
                  <li>· {it.b}</li>
                </ul>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-wafa-cream/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-wafa-deep">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-wafa-primary" />
                  {it.status}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
